// Historial de promos de venta, paginado y exportable a CSV. Global es la tabla ordenable de
// Auditoría; `compact` es el historial común del editor de una promo.
import { useState } from 'react';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ErrorState, Skeleton } from '@/kit';
import { formatDateMx, formatTimeMx } from '@/lib/dateHelpers';
import { cn } from '@/lib/cn';
import { LifecycleEventBadge, LifecycleHistory } from '@/shared/patterns/Lifecycle';
import type { SalePromotionLogEntry } from '../api/promotionsContract';
import { useExportSalePromotionLogCsv, useSalePromotionLog } from '../api/usePromotions';
import { downloadCsv, salePromotionLogFilename } from '../lib/downloadCsv';
import { actorLabel, LOG_EVENT_LABEL } from '../lib/promotionsFormat';
import { LIST_STATUS_LABEL, type PromotionListStatus } from '../lib/promotionsList';
import { MISSING_EDIT_DIFF } from '../lib/unsupportedFeatures';

type LogSortKey = 'date' | 'time' | 'actor' | 'event' | 'promo' | 'detail';

interface LogSort {
    key: LogSortKey;
    direction: 'asc' | 'desc';
}

const DEFAULT_LOG_SORT: LogSort = { key: 'date', direction: 'desc' };

function statusLabel(status: PromotionListStatus | null): string | null {
    return status === null ? null : LIST_STATUS_LABEL[status];
}

/** El motivo; sin él, el cambio de estado («Activa → Pausada»). */
export function logDetail(entry: SalePromotionLogEntry): string {
    if (entry.reason) return entry.reason;
    const from = statusLabel(entry.fromStatus);
    const to = statusLabel(entry.toStatus);
    if (from && to && from !== to) return `${from} → ${to}`;
    return '';
}

function sortValue(entry: SalePromotionLogEntry, key: LogSortKey): string {
    switch (key) {
        case 'date':
            return entry.occurredAt;
        case 'time':
            return formatTimeMx(entry.occurredAt);
        case 'actor':
            return actorLabel(entry);
        case 'event':
            return LOG_EVENT_LABEL[entry.event];
        case 'promo':
            return entry.promotionName;
        case 'detail':
            return logDetail(entry);
    }
}

export function sortLogEntries(
    entries: readonly SalePromotionLogEntry[],
    sort: LogSort,
): SalePromotionLogEntry[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...entries].sort(
        (a, b) =>
            sign * sortValue(a, sort.key).localeCompare(sortValue(b, sort.key), 'es-MX') ||
            b.occurredAt.localeCompare(a.occurredAt),
    );
}

/** Detalle de la fila; una edición sin motivo dice que el historial aún no guarda el cambio. */
function DetailText({ entry }: { entry: SalePromotionLogEntry }) {
    const detail = logDetail(entry);
    if (detail === '' && entry.event === 'UPDATE') {
        return (
            <span className="italic text-muted-foreground" title={MISSING_EDIT_DIFF}>
                Sin detalle del cambio
            </span>
        );
    }
    return <span title={detail || undefined}>{detail}</span>;
}

export function PromotionLogPanel({
    promotionId,
    retailerId,
    compact = false,
}: {
    promotionId?: string;
    /** Cadena de la promo; el admin de tenant la necesita para leer el historial. */
    retailerId?: string;
    /** Bloque corto del detalle de una promo: sin encabezados, con exportar al pie. */
    compact?: boolean;
}) {
    const log = useSalePromotionLog({ promotionId, retailerId });
    const exportCsv = useExportSalePromotionLogCsv();
    const [sort, setSort] = useState<LogSort>(DEFAULT_LOG_SORT);
    const showPromo = promotionId === undefined;

    const onExport = () => {
        exportCsv.mutate(
            { promotionId, retailerId },
            {
                onSuccess: (blob) => {
                    downloadCsv({
                        blob,
                        filename: salePromotionLogFilename(promotionId, new Date()),
                    });
                },
                onError: (error) => {
                    toast.error('No se pudo exportar el historial', {
                        description: error instanceof Error ? error.message : undefined,
                    });
                },
            },
        );
    };

    if (compact) {
        return (
            <LifecycleHistory
                title="Historial de esta promo"
                isLoading={log.isPending}
                error={
                    log.error && log.entries.length === 0 ? 'No pudimos cargar el historial.' : null
                }
                onRetry={() => {
                    void log.refetch();
                }}
                emptyText="Sin eventos registrados"
                entries={log.entries.map((entry) => ({
                    id: entry.logId,
                    at: entry.occurredAt,
                    event: entry.event,
                    detail: <DetailText entry={entry} />,
                    actor: actorLabel(entry),
                }))}
                footer={
                    <>
                        {log.hasNextPage ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[10.5px]"
                                onClick={() => {
                                    void log.fetchNextPage();
                                }}
                                isLoading={log.isFetchingNextPage}
                            >
                                Cargar más
                            </Button>
                        ) : null}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[10.5px]"
                            onClick={onExport}
                            isLoading={exportCsv.isPending}
                        >
                            Exportar CSV
                        </Button>
                    </>
                }
            />
        );
    }

    return (
        <section className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
                <span className="text-[11.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                    Publicaciones, pausas, vencimientos y ediciones
                </span>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={onExport}
                    isLoading={exportCsv.isPending}
                >
                    Exportar CSV
                </Button>
            </div>
            {renderBody()}
        </section>
    );

    function renderBody() {
        if (log.error && log.entries.length === 0) {
            return (
                <ErrorState
                    className="m-2"
                    title="No pudimos cargar el historial"
                    description={log.error instanceof Error ? log.error.message : undefined}
                    onRetry={() => {
                        void log.refetch();
                    }}
                />
            );
        }
        if (log.isPending) return <Skeleton lines={4} className="p-3" />;
        if (log.entries.length === 0) {
            return (
                <p className="px-3 py-8 text-center text-[12px] text-muted-foreground">
                    Todavía no hay eventos en el historial.
                </p>
            );
        }
        return (
            <>
                {renderTable()}
                {log.hasNextPage ? (
                    <div className="flex justify-center border-t border-border p-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                void log.fetchNextPage();
                            }}
                            isLoading={log.isFetchingNextPage}
                        >
                            Cargar más
                        </Button>
                    </div>
                ) : null}
            </>
        );
    }

    function renderTable() {
        const rows = sortLogEntries(log.entries, sort);
        return (
            <table className="w-full text-[12px]">
                <thead className="bg-muted/40 text-left text-[10.5px] uppercase tracking-[0.04em] text-muted-foreground">
                    <tr>
                        {renderHeader('date', 'Fecha')}
                        {renderHeader('time', 'Hora')}
                        {renderHeader('actor', 'Autor')}
                        {renderHeader('event', 'Evento')}
                        {showPromo ? renderHeader('promo', 'Promo') : null}
                        {renderHeader('detail', 'Detalle')}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((entry) => (
                        <tr key={entry.logId} className="border-t border-border">
                            <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                                {formatDateMx(entry.occurredAt)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                                {formatTimeMx(entry.occurredAt)}
                            </td>
                            <td className="max-w-[160px] truncate px-3 py-2">
                                {actorLabel(entry)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                                <LifecycleEventBadge event={entry.event} />
                            </td>
                            {showPromo ? (
                                <td className="max-w-[220px] px-3 py-2">
                                    <div className="truncate" title={entry.promotionName}>
                                        {entry.promotionName}
                                    </div>
                                    <div className="text-[9.5px] text-muted-foreground">
                                        Versión {entry.version}
                                    </div>
                                </td>
                            ) : null}
                            <td className="max-w-[280px] truncate px-3 py-2 text-muted-foreground">
                                <DetailText entry={entry} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    function renderHeader(key: LogSortKey, label: string) {
        const active = sort.key === key;
        const Icon = !active ? ChevronsUpDown : sort.direction === 'asc' ? ChevronUp : ChevronDown;
        return (
            <th
                className="px-3 py-2 font-semibold"
                aria-sort={
                    active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                }
            >
                <button
                    type="button"
                    className="inline-flex items-center gap-1 uppercase tracking-[0.04em] hover:text-foreground"
                    onClick={() => {
                        setSort(
                            active
                                ? { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
                                : { key, direction: 'asc' },
                        );
                    }}
                >
                    {label}
                    <Icon aria-hidden className={cn('size-3', !active && 'opacity-40')} />
                </button>
            </th>
        );
    }
}
